import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_LOGO = '/olafel_logo.png';

/**
 * Full-screen loading splash with a smoothly-zooming Olafel/admin logo.
 * - Source: admin-uploaded `loading_logo_path` (Site Settings) → falls back to /olafel_logo.png.
 * - The PATH (not a blob URL) is cached in localStorage so the pre-React
 *   `#initial-loader` and subsequent visits can render the logo instantly
 *   without waiting for the API and without ever showing a broken-image icon.
 */
export default function LoadingScreen({ minHeight = 'min-h-screen' }) {
  const buildSrc = (path) => (path ? `${API}/files/${path}` : FALLBACK_LOGO);

  const [src, setSrc] = useState(() => {
    try {
      const cachedPath = localStorage.getItem('olafel_loading_logo_path');
      if (cachedPath) return buildSrc(cachedPath);
    } catch { /* ignore */ }
    return FALLBACK_LOGO;
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site-settings`);
        const path = data?.loading_logo_path;
        if (!alive) return;
        if (path) {
          try { localStorage.setItem('olafel_loading_logo_path', path); } catch { /* ignore */ }
          setSrc(buildSrc(path));
        } else {
          try { localStorage.removeItem('olafel_loading_logo_path'); } catch { /* ignore */ }
          setSrc(FALLBACK_LOGO);
        }
      } catch { /* fallback stays */ }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div
      className={`${minHeight} w-full bg-white flex items-center justify-center`}
      data-testid="loading-screen"
    >
      <img
        src={src}
        alt="Loading"
        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_LOGO; }}
        className="w-28 h-28 object-contain olafel-zoom"
        style={{ filter: 'drop-shadow(0 6px 18px rgba(13,110,159,0.18))' }}
      />
    </div>
  );
}
